import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { toast } from 'react-toastify';
import api from '../services/api';
import { buildEmptyForm, buildPraticaPayload, PfPraticaFormLayout } from './pfPraticaFormShared';

export default function PfPraticaNew() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [lookups, setLookups] = useState({
    clienti: [],
    tipiOperazione: [],
    stati: [],
    istituti: []
  });
  const [formData, setFormData] = useState(buildEmptyForm());

  const defaultStatoId = useMemo(() => {
    const firstNonFinale = lookups.stati.find((row) => !Number(row.finale));
    return firstNonFinale?.id || lookups.stati[0]?.id || '';
  }, [lookups.stati]);

  useEffect(() => {
    const loadReferences = async () => {
      setLoading(true);
      try {
        const [tipiRes, statiRes] = await Promise.all([
          api.get('/pf-tipi-operazione'),
          api.get('/pf-stati-pratica')
        ]);

        const nextLookups = {
          clienti: [],
          tipiOperazione: Array.isArray(tipiRes.data) ? tipiRes.data : [],
          stati: Array.isArray(statiRes.data) ? statiRes.data : [],
          istituti: []
        };

        setLookups(nextLookups);

        const firstNonFinale = nextLookups.stati.find((row) => !Number(row.finale));
        const initialStatoId = firstNonFinale?.id || nextLookups.stati[0]?.id || '';
        setFormData((prev) => ({
          ...prev,
          stato_id: initialStatoId ? String(initialStatoId) : prev.stato_id
        }));
        setError('');
      } catch (err) {
        console.error('Error fetching pf pratica lookups:', err);
        setError('Errore nel caricamento dei dati di supporto');
      } finally {
        setLoading(false);
      }
    };

    loadReferences();
  }, []);

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!formData.cliente_id || !formData.tipo_operazione_id || !formData.stato_id) {
      toast.error('Cliente, tipo operazione e stato sono obbligatori');
      return;
    }

    setSaving(true);
    try {
      await api.post('/pf-pratiche', buildPraticaPayload(formData));
      toast.success('Pratica creata con successo');
      navigate('/pratiche-finanziarie/pratiche');
    } catch (err) {
      console.error('Error creating pf pratica:', err);
      const message = err?.response?.data?.message || 'Errore nel salvataggio della pratica';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <PfPraticaFormLayout
      title="Nuova pratica"
      subtitle="Inserisci dati base, scadenze e parametri finanziari della pratica"
      submitLabel="Crea pratica"
      saving={saving}
      error={error}
      formData={formData}
      lookups={lookups}
      onFormChange={handleFormChange}
      onSubmit={handleSubmit}
      onCancel={() => navigate('/pratiche-finanziarie/pratiche')}
      onReset={() => setFormData(buildEmptyForm(defaultStatoId))}
    />
  );
}
